import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ChevronDown, ScrollText } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import LifeAudio from "@/components/LifeAudio";
import { DEFAULT_CHAPTERS } from "@/lib/story";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Chapter({ ch, index }) {
  const [open, setOpen] = useState(index === 0);
  const first = ch.intro.charAt(0);
  const restIntro = ch.intro.slice(1);

  return (
    <article
      data-testid={`story-chapter-${index + 1}`}
      className="rounded-3xl border border-stone-200/70 bg-white/90 p-6 shadow-sm sm:p-8"
    >
      <header className="flex items-baseline gap-4">
        <span className="font-display text-4xl font-semibold text-[#C5A059]">
          {ch.num}
        </span>
        <div>
          <h3 className="font-display text-2xl font-semibold text-stone-900">
            {ch.title}
          </h3>
          <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">
            {ch.era}
          </p>
        </div>
      </header>

      <p className="mt-5 font-display text-[17px] leading-8 text-stone-700">
        <span className="float-left mr-2 mt-1 font-display text-5xl font-semibold leading-[0.8] text-[#C85A32]">
          {first}
        </span>
        {restIntro}
      </p>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            {ch.rest.map((p, pi) => (
              <p
                key={pi}
                className="mt-4 font-display text-[17px] leading-8 text-stone-700"
              >
                {p}
              </p>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid={`story-toggle-${index + 1}`}
        onClick={() => setOpen(!open)}
        className="mt-5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#8a6b2e] transition-colors duration-300 hover:text-[#C85A32]"
      >
        {open ? "Réduire" : "Lire la suite"}
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
    </article>
  );
}

export default function Story() {
  const [chapters, setChapters] = useState(DEFAULT_CHAPTERS);

  useEffect(() => {
    axios
      .get(`${API}/story`)
      .then((r) => {
        if (r.data.chapters) setChapters(r.data.chapters);
      })
      .catch(() => {});
  }, []);
  return (
    <section
      id="histoire"
      data-testid="story-section"
      className="bg-[#F4EFEA]/90 px-4 py-16 sm:py-24"
    >
      <div className="mx-auto max-w-3xl">
        <Reveal className="mb-10 text-center">
          <p className="flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C5A059]">
            <ScrollText className="h-4 w-4" /> Sa vie, racontée
          </p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-stone-900 sm:text-4xl">
            L'Histoire du Patriarche
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-stone-500 sm:text-base">
            Quatre chapitres écrits par les siens — de Banigbé-Gare à
            l'éternité.
          </p>
        </Reveal>

        <Reveal className="mb-10">
          <LifeAudio />
        </Reveal>

        <div className="space-y-5">
          {chapters.map((ch, i) => (
            <Reveal key={ch.num} delay={i * 0.06}>
              <Chapter ch={ch} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
